import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Airplane, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/admin", { replace: true });
  }, [loading, user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Hoş geldiniz, Yönetici.");
      navigate("/admin", { replace: true });
    } catch (e) {
      const raw = formatApiErrorDetail(e?.response?.data?.detail) || e.message || "";
      const msg = /invalid email or password/i.test(raw)
        ? "E-posta veya şifre hatalı. Lütfen tekrar deneyin."
        : raw || "Giriş başarısız. Lütfen daha sonra tekrar deneyin.";
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex flex-col px-8 lg:px-16 py-10">
        <a href="/" className="flex items-center gap-3" data-testid="login-brand-link">
          <div className="h-9 w-9 bg-[#0B2046] flex items-center justify-center">
            <Airplane size={18} weight="fill" className="text-white -rotate-45" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-black tracking-tighter text-[15px] text-slate-900">IAVA</div>
            <div className="label-eyebrow text-[9px]">Yönetici Konsolu</div>
          </div>
        </a>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            <span className="label-eyebrow">/ Güvenli Erişim</span>
            <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-4">
              Yönetici<br />girişi.
            </h1>
            <p className="mt-4 text-slate-600">
              Bu konsol sadece sertifika düzenleyen yetkililer içindir. Tüm işlemler kayıt altına alınır.
            </p>

            <form onSubmit={onSubmit} className="mt-10 space-y-5">
              <div>
                <label className="label-eyebrow block mb-2">E-posta</label>
                <input
                  data-testid="admin-login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3.5 border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#0B2046] focus:border-[#0B2046] bg-white text-[15px]"
                  placeholder="admin@gacvs.aero"
                  required
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Şifre</label>
                <input
                  data-testid="admin-login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3.5 border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#0B2046] focus:border-[#0B2046] bg-white text-[15px]"
                  placeholder="••••••••"
                  required
                />
              </div>

              {err && (
                <div data-testid="admin-login-error" className="text-sm text-red-600">{err}</div>
              )}

              <button
                data-testid="admin-login-submit"
                type="submit"
                disabled={submitting}
                className="w-full px-5 py-3.5 bg-[#0B2046] text-white hover:bg-[#113066] transition-colors inline-flex items-center justify-center gap-2 font-medium disabled:opacity-60"
              >
                {submitting ? "Doğrulanıyor…" : (<>Konsola giriş yap <ArrowRight size={16} weight="bold" /></>)}
              </button>
            </form>

            <div className="mt-10 text-xs text-slate-500 font-mono-plex">
              IP-tabanlı · 8 saatlik oturum · ICAO Annex 17 uyumlu
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/4318372/pexels-photo-4318372.jpeg"
          alt="Havalimanı terminali"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0B2046]/70" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <div className="label-eyebrow text-slate-300">Düzenleme Terminali</div>
          <div className="font-display text-3xl font-black tracking-tighter mt-3 max-w-md leading-tight">
            &ldquo;Belgeye değil, kayıt sistemine güveniyoruz.&rdquo;
          </div>
          <div className="label-eyebrow text-slate-400 mt-6">— IAVA Tüzüğü, Madde II</div>
        </div>
      </div>
    </div>
  );
}
